import {lazy} from './lang.js';
import {t_double} from './t_double.js';

export const rules = (... list) => {
    let lastRules = undefined;
    return (...args) => {
        let _list = [];
        let changed = false;
        let i=0;
        list = list.map(x => lazy(x, ...args)).flat(4);
        for (let rule of list) {
            let _rule = {};
            for (let key in rule) {
                _rule[key] = lazy(rule[key], ...args);
                if (key !== "counter" && lastRules && lastRules[i] && lastRules[i][key]) {
                    if (typeof _rule[key] !== "object" && lastRules[i][key] !== _rule[key]) {
                        changed = true;
                    }
                }
            }
            _list.push(_rule);
            i++;
        }
        if (!changed && lastRules) {
            return lastRules;
        }
        lastRules = _list;
        return _list;
    };
};

const then = (rule, depth=0) => {
    let ret = {
        decide: (action) => ({
            ... rule,
            decide: action,
        }),
        always: (action) => ({
            ... rule,
            always: action,
        }),
        alt: (...actions) => ({
            ... rule,
            alt: actions,
        }),
        series: (...actions) => ({
            ... rule,
            series: actions,
        }),
        never: (action) => ({
            ... rule,
            never: action,
        })
    };
    if (depth <= 3) {
        ret.later = (delay) => then(
            {
                ...rule,
                delay
            },
            depth++);
        ret.first = (firstOffset=0) => then(
            {
                ... rule,
                firstOffset
            },
            depth++);
        ret.last = (lastOffset=0) => then(
            {
                ... rule,
                lastOffset
            },
            depth++);
        ret.figure = (figureFrom, figureTo) => then(
            {
                ...rule,
                figureFrom,
                figureTo,
            },
            depth++);
        ret.every = (every, offset=0) => then(
            {
                ...rule,
                every,
                offset
            },
            depth++);
        ret.max = (max) => then(
            {
                ...rule,
                max
            },
            depth++);
        ret.skip = (skip) => then(
            {
                ...rule,
                skip
            },
            depth++);
    }
    return ret;
};

export const otherwise = () => {
   let rule = {
       otherwise: true,
       every: 1,
       offset: 0,
       counter: 0
    };

    return {
        ... rule,
        ... then(rule)
    };
};

export const when = (predicate) => {
   let rule = {
       when: predicate,
       every: 1,
       offset: 0,
       counter: 0
    };

    return {
        ... rule,
        ... then(rule)
    };
};

export const before = (predicate) => {
    let rule = {
        before: predicate,
        every: 1,
        offset: 0,
        counter: 0
    };

    return {
        ... rule,
        ... then(rule)
    };
};

export const after = (predicate) => {
    let rule = {
        after: predicate,
        every: 1,
        offset: 0,
        counter: 0
    };

    return {
        ... rule,
        ... then(rule)
    };
};


export const applyRule = (
    rulePredicate,
    ruleBook,
    decide,
    figureCount,
    nthFigure,
    totalFigures,
    dontCount=false
) => {
    if (!ruleBook) {
        // if no rule book then no actions
        return [];
    }

    let actions = [];
    let delayedActions = [];
    let matchedRules = [];
    for (let rule of ruleBook) {
        if (predicatesMatch(rulePredicate, rule)) {
            // consult how to proceed when predicates match
            // can either call 1. decide, 2. never do something
            // or 3. always do something
            let {firstOffset, lastOffset, delay, every, offset, max, skip, figureFrom, figureTo} = rule;

            if (figureFrom !== undefined) {
                if (figureTo === undefined && figureFrom !== figureCount) {
                    continue;
                }
                if (figureTo !== undefined && (
                    figureFrom > figureCount || figureCount > figureTo)) {
                    continue;
                }
            }

            if (firstOffset !== undefined && nthFigure !== firstOffset && firstOffset >= 0) {
                continue;
            }

            if (lastOffset !== undefined && totalFigures - nthFigure  - 1 !== lastOffset && lastOffset >= 0) {
                continue;
            }

            if (!dontCount) {
                rule.counter++;
            }

            if (rule.max && rule.counter > rule.max) {
                // case: after(dash(1)).max(2).always(ACCENT)
                // means we only do an accent at most twice
                // So here, we "continue", since we have applied
                // the rule more than the max-allowed # of times
                continue;
            }

            if (rule.skip !== undefined && rule.counter <= rule.skip) {
                // case: after(dash(1)).max(2).always(ACCENT)
                // means we only do an accent at most twice
                // So here, we "continue", since we have applied
                // the rule more than the max-allowed # of times
                continue;
            }

            if ((rule.counter - 1) % every !== offset) {
                // only apply rule when the "every" condition
                // is met
                // for example, sometimes we want to apply a rule
                // every 2 times the condition is met.
                // For instance every 2 dashes do an accent afterwards
                continue;
            }

            matchedRules.push(rule);

            if (rule.decide) {
                if (decide()) {
                    if (delay) {
                        delayedActions.push({
                            delay,
                            ...rule.decide
                        });
                    } else {
                        actions.push(rule.decide);
                    }
                }
            } else if (rule.always) {
                if (delay) {
                    delayedActions.push({
                        delay,
                        ... rule.always
                    });
                } else {
                    if (Array.isArray(rule.always)) {
                        rule.always.forEach(
                            a => actions.push(a));
                    } else {
                        actions.push(rule.always);
                    }
                }
            } else if (rule.alt) {
                let alt = rule.alt;
                if (delay) {
                    delayedActions.push({
                        delay,
                        ... alt[(rule.counter - 1) % alt.length]
                    });
                } else {
                    actions.push(alt[(rule.counter - 1) % alt.length]);
                }
            } else if (rule.series) {
                rule.series.forEach(
                    (action, delay) => {
                        let _actions = Array.isArray(action) ? action : [action];
                        _actions.forEach(
                            a => {
                                if (delay === 0) {
                                    actions.push(a);
                                } else {
                                    delayedActions.push({
                                        delay,
                                        ... a
                                    });
                                }
                            });
                    }); 
            } else if (rule.never) {
                // dont push here since we're avoiding?
                // should this even exist then?
            }
        }
    }
    return {actions, delayedActions, matchedRules};
};

export const ANY = {
    type: "any",
    value: ""
};

export const ACCENT = {
    type: "accent",
    value: ""
};

export const FLAM = {
    type: "flam",
    value: ""
};

export const REGULAR = {
    type: "regular",
    value: ""
};

export const LEGATO = {
    type: "legato",
    value: ""
};

export const STACCATO = {
    type: "staccato",
    value: ""
};

export const PIANISSIMO = {
    type: "pianissimo",
    value: ""
};

export const PIANO = {
    type: "piano",
    value: ""
};

export const REST = {
    type: "rest",
    value: ""
};

export const DOUBLE = {
    type: "double",
    value: ""
};

export const TIMES = {
   type: "times",
    value: ""
};

// rule 1 is the current state of the world
// rule 2 is from the rule book
export const predicatesMatch = (rule1, rule2) => {
    if (rule1.when && rule2.when) {
        return rule1.when.type === rule2.when.type &&
            rule1.when.value === rule2.when.value;
    }
    if (rule1.before && rule2.before) {
        return rule1.before.type === rule2.before.type &&
            rule1.before.value === rule2.before.value;
    }
    if (rule1.after && rule2.after) {
        return rule1.after.type === rule2.after.type &&
            rule1.after.value === rule2.after.value;
    }
    if (rule1.otherwise && rule2.otherwise) {
        return true;
    }
    return false;
}

export const applyToLeaf = (leaf, action) => {
    if (Array.isArray(leaf)) {
        // we dont apply to arrays (i.e. doubled leaves)
        if (action.type === "regular") {
            return leaf[0];
        }
        return leaf;
    }
    leaf = {... leaf};
    switch (action.type) {
    case "flam":
        leaf.flam = true;
        break;
    case "regular":
        leaf.staccato = false;
        leaf.transpose = 0;
        leaf.accent = false;
        leaf.flam = false;
        leaf.piano = false;
        leaf.pianissimo = false;
        break;
    case "staccato":
        leaf.staccato = true;
        leaf.transpose = 0;
        break;
    case "legato":
        leaf.legato = true;
        leaf.staccato = false;
        leaf.transpose = 0;
        break;
    case "accent":
        leaf.accent = true;
        leaf.pianissimo = false;
        leaf.piano  = false;
        leaf.transpose = 0;
        break;
    case "rest":
        leaf.transpose = null;
        leaf.accent = false;
        break;
    case "pianissimo":
        leaf.pianissimo = true;
        leaf.accent = false;
        leaf.transpose = 0;
        break;
    case "piano":
        leaf.piano = true;
        leaf.accent = false;
        leaf.transpose = 0;
        break;
    case "double":
        if (leaf.transpose === null) {
            leaf.transpose = 0;
        }
        leaf = [leaf, leaf];
        break;
    case "times":
        leaf = new Array(action.value).fill(leaf);
        break;
    }
    
    return leaf;
};
